import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

// Detect if running embedded in KUOS main app
const isEmbedded = window.parent !== window

// API base URL - use relative path when embedded
const api = axios.create({
  baseURL: isEmbedded ? '/mail-api' : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor
api.interceptors.request.use((config) => {
  // Try to get token from localStorage or from parent window
  let token = localStorage.getItem('token')
  
  // If embedded, try to get token from parent
  if (isEmbedded && !token) {
    // Request token from parent
    window.parent.postMessage({ type: 'KUOS_MAIL_GET_TOKEN' }, '*')
    // For now, use a temporary approach - in production implement proper handshake
    token = sessionStorage.getItem('kuos_token')
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  return config
})

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Avoid redirect loop if already on login page
      const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/login/'
      if (isLoginPage) {
        // Just log out silently, don't reload
        const authStore = useAuthStore()
        authStore.logout()
      } else if (isEmbedded) {
        // If embedded, notify parent to re-authenticate
        window.parent.postMessage({ type: 'KUOS_MAIL_AUTH_REQUIRED' }, '*')
      } else {
        const authStore = useAuthStore()
        authStore.logout()
        window.location.href = '/login'
      }
    }
    if (error.response?.status === 429) {
      console.warn('[API] Rate limited:', error.response?.data?.message || 'Too many requests')
      // Don't show alert spam — just log it. The UI should handle retries gracefully.
    }
    return Promise.reject(error)
  }
)

// Listen for messages from parent window
if (isEmbedded) {
  window.addEventListener('message', (event) => {
    if (event.data?.type === 'KUOS_TOKEN') {
      sessionStorage.setItem('kuos_token', event.data.token)
    }
    
    // Handle compose email request from parent
    if (event.data?.type === 'KUOS_MAIL_COMPOSE') {
      // Store compose data for the app to pick up
      sessionStorage.setItem('mail_compose_data', JSON.stringify(event.data))
      // Trigger navigation to compose if needed
      window.dispatchEvent(new CustomEvent('kuos-mail-compose', { detail: event.data }))
    }
  })
}

export default api
